/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Box, Badge} from '@salesforce/retail-react-app/app/components/shared/ui'
import {BIRTHDAY_50_OFF, BIRTHDAY_25_OFF} from '../../constants'
import {
    selectVisiblePriceAdjustments,
    selectBirthdayPriceAdjustments,
    selectShippingPromotions,
    showFinalSaleBadge
} from '../../utils/product-utils'
import {useLocation} from 'react-router-dom'

const ItemBadges = ({
    product,
    showInventoryMessage = false,
    showBopisInventoryMessage = false,
    showGlobaleInventoryMessage = false
}) => {
    const location = useLocation()
    const isCheckoutPage = location?.pathname?.includes('/checkout')

    // Selectors are shared with the GTM item_badge param so the tracked badges match the
    // rendered ones — see getLineItemBadge in utils/product-utils.
    const visiblePriceAdjustments = selectVisiblePriceAdjustments(product)
    const hasPriceAdjustments = visiblePriceAdjustments.length > 0

    const showFinalSaleMsg = showFinalSaleBadge(product)

    const shippingPromotions = selectShippingPromotions(product)
    const birthdayLabelMap = {
        LOYALTY_BDAY_50: BIRTHDAY_50_OFF,
        LOYALTY_BDAY_25: BIRTHDAY_25_OFF
    }

    // take birthday promotions from priceAdjustments as it's showing only if applied
    const birthdayPromotions = selectBirthdayPriceAdjustments(product)

    const showAddOnItem = product?.bonusProductLineItem
    const shouldShow =
        showFinalSaleMsg ||
        hasPriceAdjustments ||
        shippingPromotions.length > 0 ||
        birthdayPromotions.length > 0 ||
        showAddOnItem ||
        showInventoryMessage ||
        showBopisInventoryMessage ||
        showGlobaleInventoryMessage

    // This function determines the label to show on the badge based on the type of price adjustment.
    const renderAdjustmentLabel = (adjustment) => {
        // Prefer explicit itemText when available
        if (adjustment?.itemText) return adjustment.itemText

        const applied = adjustment?.appliedDiscount
        if (!applied) return 'Discount'
    }
    // This function determines badge styles based on the type of price adjustment.
    const getBadgeStyles = (adjustment) => {
        const applied = adjustment?.appliedDiscount
        const type = (applied?.type || '').toLowerCase()
        // add more types as needed, default to gray for unknown types
        switch (type) {
            case 'percentage':
                return {color: 'green.500', backgroundColor: 'green.100'}
            default:
                return {color: 'gray.800', backgroundColor: 'gray.50'}
        }
    }

    return (
        <Box
            className="item-badges"
            width="fit-content"
            display={shouldShow ? 'flex' : 'none'}
            flexWrap="wrap"
            gap="10px"
        >
            {hasPriceAdjustments &&
                visiblePriceAdjustments.map((adjustment, idx) => {
                    const styles = getBadgeStyles(adjustment)
                    return (
                        <Badge
                            key={`pa-${idx}`}
                            color={styles.color}
                            backgroundColor={styles.backgroundColor}
                            textTransform="capitalize"
                            padding="4px 8px"
                            fontSize="13px"
                            fontWeight="400"
                            letterSpacing="0.065px"
                            lineHeight="22px"
                        >
                            {renderAdjustmentLabel(adjustment)}
                        </Badge>
                    )
                })}

            {showFinalSaleMsg && (
                <Badge
                    color="red.950"
                    backgroundColor="red.18"
                    textTransform="capitalize"
                    padding="4px 8px"
                    fontSize="13px"
                    fontWeight="400"
                    letterSpacing="0.065px"
                    lineHeight="22px"
                >
                    <FormattedMessage id="badge.final.sale" defaultMessage="Final Sale" />
                </Badge>
            )}

            {(showBopisInventoryMessage || showInventoryMessage || showGlobaleInventoryMessage) && (
                <Badge
                    color="red.950"
                    borderRadius="3px"
                    border="1px solid"
                    borderColor="red.950"
                    className="oos-badge"
                    backgroundColor="white"
                    textTransform="unset"
                    padding="4px 8px"
                    fontSize="13px"
                    fontWeight="400"
                    letterSpacing="0.065px"
                    lineHeight="22px"
                >
                    <FormattedMessage id="out.of.stock" defaultMessage="Out of Stock" />
                </Badge>
            )}

            {!isCheckoutPage &&
                shippingPromotions.map((shipping, idx) => (
                    <Badge
                        key={`ship-${idx}`}
                        color="gray.800"
                        backgroundColor="gray.50"
                        textTransform="capitalize"
                        padding="4px 8px"
                        fontSize="13px"
                        fontWeight="400"
                        letterSpacing="0.065px"
                        lineHeight="22px"
                    >
                        {shipping.name}
                    </Badge>
                ))}

            {birthdayPromotions.map((birthday, idx) => (
                <Badge
                    key={`birth-${idx}`}
                    color="purple.700"
                    backgroundColor="purple.75"
                    textTransform="capitalize"
                    padding="4px 8px"
                    fontSize="13px"
                    fontWeight="400"
                    letterSpacing="0.065px"
                    lineHeight="22px"
                >
                    {birthdayLabelMap[birthday.promotionId]}
                </Badge>
            ))}

            {showAddOnItem && (
                <Badge
                    color="green.500"
                    backgroundColor="green.100"
                    textTransform="capitalize"
                    padding="4px 8px"
                    fontSize="13px"
                    fontWeight="400"
                    letterSpacing="0.065px"
                    lineHeight="22px"
                >
                    <FormattedMessage id="badge.add.on.item" defaultMessage="Add-On Item" />
                </Badge>
            )}
        </Box>
    )
}

ItemBadges.propTypes = {
    product: PropTypes.object,
    showBopisInventoryMessage: PropTypes.bool,
    showGlobaleInventoryMessage: PropTypes.bool,
    showInventoryMessage: PropTypes.bool
}

export default ItemBadges
